import { verifyOfferIntent } from './offer-intent'
import { offerApi, rememberNatOffer, USE_V2_OFFERS_PREVIEW } from './offer-api'
import { OfferError, OFFER_PRE_BROADCAST_CODES } from './offer-errors'
import { OFFER_COSIGNER_PUBLIC_KEY, OFFER_MARKET_FEE_ADDRESS } from './offer-constants'
import {
	assertSignedEscrow,
	assertSignedFunding,
	NOT_PLACED,
	SIGHASH_ALL,
	SIGHASH_NONE_ANYONECANPAY,
	validateOfferAcceptTemplate,
	validateOfferFundingTemplate,
	validateOfferScopeTemplate
} from './offer-templates'
import { fetchLiveItemOutpoint, type LiveItemOutpoint } from './offer-item'
import { validateOfferQuote } from './offer-quote'
export { OfferError, OFFER_ERROR_COPY } from './offer-errors'
/** Collection-offer API client extracted from the broader offers preview.
 * Collection/trait escrow uses NONE|ANYONECANPAY; the service co-signature
 * enforces item eligibility and the fill policy. See docs/collection-offers.md.
 */
import { NAT_COLLECTION, natTerms, type NatTerms } from './nat-offers'
import { Buffer } from 'buffer'
import { Transaction, script as bitcoinScript } from 'bitcoinjs-lib'
import { API_URL, OFFERS_API_URL, FEATURE_FLAGS } from '@/utils/constants'
import { OFFERS_PREVIEW_BUILD } from './offer-env'
import Wallet from '@/utils/wallet'
import { signOfferPsbts } from '@/utils/offer-sign'
import { parsePsbt } from '@/utils/market/secure-psbt'
import { readOffers } from './offer-read'
import { beginOfferSigning } from '@/utils/wallet/offer-signing-guard'
import { backupFromPsbt, recoveryScripts, saveRecoveryBackup } from './offer-recovery'
import { ensureEccLib } from './wallet'
import { owContextHeaders } from '@/app/alkanes/lib/ow-context-headers'
import isFungible from './is-fungible'
import isRune from './is-rune'
import isAlkanes from './is-alkanes'

export type OfferState =
	| 'building'
	| 'active'
	| 'accepted'
	| 'rejected'
	| 'cancelled'
	| 'expired'
	| 'stale'
	| 'settling'
	| 'cancelling'

/** Sentence-case copy for an offer state wherever a state is shown as text. */
export const OFFER_STATE_LABELS: Record<OfferState, string> = {
	building: 'Building',
	active: 'Active',
	accepted: 'Accepted',
	rejected: 'Declined',
	cancelled: 'Cancelled',
	expired: 'Expired',
	stale: 'Stale',
	settling: 'Settling',
	cancelling: 'Cancelling'
}

export const offerStateLabel = (state: OfferState | string): string =>
	OFFER_STATE_LABELS[state as OfferState] ?? state

/**
 * The API voids an offer whose funding was replaced or evicted before it
 * confirmed: state `cancelled` with no `cancel_txid` (nothing left the
 * buyer's wallet) on an offer that had a funding transaction. There is no
 * escrow to settle or refund.
 */
export const offerFundingReplaced = (
	offer: Pick<Offer, 'state' | 'cancel_txid' | 'funding_txid'> | null | undefined
): boolean => !!offer && offer.state === 'cancelled' && !offer.cancel_txid && !!offer.funding_txid

/** The status word for an offer: its state, or "Funding replaced" for a voided one. */
export const offerStatusLabel = (offer: Pick<Offer, 'state' | 'cancel_txid' | 'funding_txid'>): string =>
	offerFundingReplaced(offer) ? 'Funding replaced' : offerStateLabel(offer.state)

/** States with nothing left to settle or refund. */
export const OFFER_TERMINAL_STATES: ReadonlySet<string> = new Set(['accepted', 'cancelled'])

export const MAX_OFFER_MESSAGE_LENGTH = 500

export type OfferScope = 'item' | 'collection' | 'trait'

export interface Offer {
	authorization_version?: number
	nat_terms?: NatTerms | null
	message?: string | null
	id: string
	scope: OfferScope
	/** Null for collection and trait offers until they are filled. */
	inscription_id: string | null
	item_outpoint: string | null
	item_value: number
	seller_address: string
	collection_slug: string | null
	trait_type: string | null
	trait_value: string | null
	filled_inscription_id: string | null
	buyer_address: string
	buyer_payment_address: string
	price_sats: number
	market_fee_sats: number
	/** Market fee the buyer was spared (bid rebate); `market_fee_sats` is net of it. */
	rebate_sats?: number
	network_fee_sats: number
	escrow_value: number
	total_sats: number
	funding_txid: string | null
	funding_vout: number | null
	/** The reconciler is still waiting on the funding transaction; the funding's inputs stay reserved. */
	funding_pending?: boolean
	recovery_delay_blocks: number
	state: OfferState
	accepted_txid: string | null
	cancel_txid: string | null
	expires_at: string
	created_at: string
	updated_at: string
	/** When the fill was settled; only on recent-offer rows for accepted/settling offers. */
	accepted_at?: string | null
}

interface OfferBuildResponse {
	authorization_version?: number
	intent_commitment?: string
	intent_fields?: unknown
	nat_terms?: NatTerms | null
	version: number
	offer_id: string
	funding_psbt: string
	batch_accept?: { accept_psbt: string; sign_input_index: number } | null
	escrow_address: string | null
	escrow_value: number
	price_sats: number
	market_fee_sats: number
	market_fee_full_sats?: number
	rebate_sats?: number
	rebate_bps?: number
	network_fee_sats: number
	validity_days: number
	recovery_delay_blocks: number
	expires_at: string
}

// OfferError and the code-to-copy map live in ./offer-errors so the template
// validators and the signing adapter can throw them without a module cycle.

/**
 * Offers are only exposed on preview deployments (or with the `offers`
 * feature flag) until the flow has been tested end to end.
 */
/**
 * Offers are on when the `offers` feature flag is set, on every Vercel
 * preview deployment, and in local development. The gate is decided at build
 * time from the environment, never from the hostname: production's own
 * *.vercel.app deployment URLs serve the production bundle and must follow the
 * production flag, so turning the flag off really turns offers off.
 */
export function offersEnabled(): boolean {
	if (FEATURE_FLAGS.includes('offers')) return true
	return OFFERS_PREVIEW_BUILD
}

/**
 * Bids on fungible tokens (TAP, BRC-20, Runes, Alkanes, DMT-NAT lots) are a
 * later rollout: their settlement needs exact-lot transfer verification that
 * the NFT flow does not. Until then every offer surface is NFT-only, and the
 * `offers::fungible` feature flag re-opens them for testing.
 */
export function fungibleOffersEnabled(): boolean {
	return FEATURE_FLAGS.includes('offers::fungible')
}

/**
 * Posting a bid's note to the collection's Social feed is parked: from a
 * fresh browser it costs the buyer a Twetch sign-in signature plus a post
 * signature on top of the bid's two, and we want bids to stay two prompts.
 * The `offers::social` feature flag re-opens the field for testing until
 * the post can ride on a signature the bid already makes.
 */
export function offerSocialPostEnabled(): boolean {
	return FEATURE_FLAGS.includes('offers::social')
}

/** Whether a collection slug or an inscription is a fungible-token lot. */
export function offerTargetIsFungible(
	slug?: string | null,
	inscription?: {
		collection?: { slug?: string } | null
		collection_slugs?: string[] | null
		brc20?: { ticker?: string } | null
		ticker?: string | null
		rune?: unknown
		alkane?: unknown
	} | null
): boolean {
	const slugs = [
		slug,
		inscription?.collection?.slug,
		...(Array.isArray(inscription?.collection_slugs) ? inscription!.collection_slugs! : [])
	].filter((value): value is string => typeof value === 'string' && value.length > 0)
	if (slugs.some((value) => isFungible(value) || isRune(value) || isAlkanes(value))) return true
	if (inscription?.brc20?.ticker || inscription?.ticker) return true
	if (inscription?.rune || inscription?.alkane) return true
	return false
}

/**
 * The gate every offer surface uses: offers are on for this deployment and
 * the target is an NFT (or fungible offers are explicitly enabled).
 */
export function offersEnabledFor(
	slug?: string | null,
	inscription?: Parameters<typeof offerTargetIsFungible>[1]
): boolean {
	if (!offersEnabled()) return false
	if (fungibleOffersEnabled()) return true
	return !offerTargetIsFungible(slug, inscription)
}

async function post<T>(path: string, body: unknown): Promise<T> {
	const offerId = /^\/market\/offers\/([0-9a-f-]{36})\//i.exec(path)?.[1]
	let res: Response
	try {
		res = await fetch(`${offerApi(path, body)}${path}`, {
			method: 'POST',
			headers: { 'content-type': 'application/json', ...owContextHeaders() },
			cache: 'no-store',
			body: JSON.stringify(body)
		})
	} catch (cause) {
		throw new OfferError('network_unreachable', 'Could not reach the offers service. Check your connection and try again. Nothing was sent.', {
			detail: cause instanceof Error ? cause.message : String(cause),
			offerId
		})
	}
	// Read the body once as text: a JSON error carries a code, anything else
	// (an extractor rejection, a proxy page) is kept verbatim for telemetry.
	let text = ''
	let data: any = null
	if (typeof res.text === 'function') {
		text = await res.text().catch(() => '')
		try {
			data = text ? JSON.parse(text) : null
		} catch {}
	} else {
		try {
			data = await res.json()
		} catch {}
	}
	if (!res.ok || data?.error) {
		const detail = [
			typeof data?.message === 'string' ? data.message : undefined,
			typeof data?.detail === 'string' ? data.detail : undefined,
			!data && text ? text.slice(0, 200) : undefined
		]
			.filter(Boolean)
			.join(' — ')
		throw new OfferError(data?.code || `http_${res.status}`, data?.message, {
			status: res.status,
			detail: detail || undefined,
			offerId
		})
	}
	return data as T
}

export interface CollectionOffers {
	slug: string
	summary: {
		count: number
		item_count: number
		collection_count: number
		trait_count: number
		top_price_sats: number
		top_collection_sats: number
		total_sats: number
	}
	offers: Offer[]
	collection_offers: Offer[]
	trait_offers: Offer[]
}

/**
 * Every offer that moved recently, across collections and both offers APIs
 * (the main API and the isolated preview), deduped by id, newest first. The
 * bid book and its activity stream are built from this.
 */
export async function fetchRecentOffers(limit = 200): Promise<Offer[]> {
	const path = `/market/offers/recent?limit=${limit}`
	const [primary, preview] = await Promise.allSettled([
		readOffers<{ offers: Offer[] }>(path, API_URL),
		OFFERS_API_URL !== API_URL
			? readOffers<{ offers: Offer[] }>(path, OFFERS_API_URL)
			: Promise.resolve({ offers: [] })
	])
	const rows = [
		...(primary.status === 'fulfilled' ? primary.value.offers || [] : []),
		...(preview.status === 'fulfilled' ? preview.value.offers || [] : [])
	]
	if (primary.status === 'rejected' && preview.status === 'rejected') throw primary.reason
	const seen = new Set<string>()
	for (const offer of rows) if (offer.nat_terms || USE_V2_OFFERS_PREVIEW) rememberNatOffer(offer.id)
	return rows
		.filter((o) => (seen.has(o.id) ? false : (seen.add(o.id), true)))
		.sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
}

export function fetchOffersForCollection(slug: string): Promise<CollectionOffers> {
	return readOffers<CollectionOffers>(
		`/market/offers/collection/${encodeURIComponent(slug)}`,
		slug === NAT_COLLECTION || USE_V2_OFFERS_PREVIEW ? OFFERS_API_URL : API_URL
	).then((data) => {
		for (const offer of [
			...(data.offers || []),
			...(data.collection_offers || []),
			...(data.trait_offers || [])
		])
			if (offer.nat_terms || USE_V2_OFFERS_PREVIEW) rememberNatOffer(offer.id)
		return data
	})
}

export type OfferStep =
	| 'building'
	| 'sign_funding'
	| 'preparing'
	| 'sign_offer'
	| 'activating'
	| 'done'

function ownedInputPlan(psbt: string) {
	const parsed = parsePsbt(psbt)
	return { psbt, indexes: Array.from({ length: parsed.inputCount }, (_, i) => i) }
}

/**
 * Full buyer flow. UniSat batches eligible funding and escrow signatures.
 * Nothing is broadcast until both are signed and the funding outpoint is verified.
 */
export interface MakeOfferParams {
	natTerms?: NatTerms
	scope?: OfferScope
	/** Required for item scope. */
	inscriptionId?: string
	/** Required for collection and trait scope. */
	collectionSlug?: string
	traitType?: string
	traitValue?: string
	priceSats: number
	feeRate: number
	validityDays: number
	message?: string
}

export async function makeOffer(
	params: MakeOfferParams,
	onStep?: (step: OfferStep) => void,
	batch?: { usedInputs: Set<string>; buyer: string },
	signal?: AbortSignal
): Promise<Offer> {
	if (params.collectionSlug === NAT_COLLECTION || params.natTerms) {
		const terms = params.natTerms
		if (
			params.scope !== 'collection' ||
			params.collectionSlug !== NAT_COLLECTION ||
			!terms ||
			terms.protocol !== 'tap' ||
			terms.ticker !== 'DMT-NAT' ||
			terms.total_price_sats !== String(params.priceSats) ||
			natTerms(terms.token_amount, params.priceSats)?.sats_per_nat !== terms.sats_per_nat
		)
			throw new OfferError('invalid_nat_terms')
	}
	const message = params.message?.trim() || undefined
	if (message && Array.from(message).length > MAX_OFFER_MESSAGE_LENGTH)
		throw new OfferError('message_too_long')
	if (!fungibleOffersEnabled() && offerTargetIsFungible(params.collectionSlug))
		throw new OfferError('fungible_offers_unavailable')
	const release = beginOfferSigning()
	try {
		signal?.throwIfAborted()
		const taproot = Wallet.getTaprootAddress()
		const ordinalKey = Wallet.taprootPaymentPublicKey() || Wallet.getPublicKey()
		const publicKey = /^[0-9a-f]{64}$/i.test(ordinalKey) ? `02${ordinalKey}` : ordinalKey
		const paymentAddress = Wallet.balanceAddress() || taproot
		const paymentPublicKey = Wallet.balancePublicKey() || publicKey
		if (!taproot || !publicKey) throw new OfferError('invalid_buyer')
		const buyer = JSON.stringify([taproot, publicKey, paymentAddress, paymentPublicKey])
		if (batch && buyer !== batch.buyer) throw new OfferError('batch_wallet_changed')

		onStep?.('building')
		const scope: OfferScope = params.scope ?? 'item'
		const buildBody = {
			scope,
			nat_terms: params.natTerms,
			inscription_id: params.inscriptionId,
			collection_slug: params.collectionSlug,
			trait_type: params.traitType,
			trait_value: params.traitValue,
			buyer_address: taproot,
			buyer_public_key: publicKey,
			buyer_payment_address: paymentAddress,
			buyer_payment_public_key: paymentPublicKey,
			price_sats: Math.round(params.priceSats),
			fee_rate: params.feeRate,
			validity_days: params.validityDays,
			message
		}
		const build = await post<OfferBuildResponse>('/market/offers/build', buildBody)
		if (build.authorization_version !== 2 || !build.intent_commitment)
			throw new OfferError('offer_authorization_upgrade_required')
		validateOfferQuote(build, params, scope)
		// An item offer's sale can only ever spend the output the item sits in
		// now, so read it fresh from the same API that built the offer and
		// require the intent to name exactly that output, value and owner.
		const itemBase = offerApi('/market/offers/build', buildBody)
		const lookItem = async (): Promise<LiveItemOutpoint | null> =>
			scope === 'item' && params.inscriptionId
				? fetchLiveItemOutpoint(params.inscriptionId, itemBase, { signal })
				: null
		const verifyIntent = (live: LiveItemOutpoint | null) => verifyOfferIntent(build.intent_fields, build.intent_commitment!, {
			inscriptionId: params.inscriptionId,
			itemOutpoint: live?.outpoint ?? null,
			itemValue: live?.sats ?? null,
			sellerAddress: live?.owner ?? null,
			id: build.offer_id,
			buyerKey: publicKey,
			paymentKey: paymentPublicKey,
			buyer: taproot,
			refund: paymentAddress,
			scope: params.scope ?? 'item',
			collection: params.collectionSlug ?? null,
			traitType: params.traitType ?? null,
			traitValue: params.traitValue ?? null,
			price: params.priceSats,
			marketFee: build.market_fee_sats,
			networkFee: build.network_fee_sats,
			escrow: build.escrow_value,
			expiry: build.expires_at,
			delay: build.recovery_delay_blocks,
			message: message ?? null,
			nat: params.natTerms
				? [
						params.natTerms.protocol,
						params.natTerms.ticker,
						params.natTerms.token_amount,
						params.natTerms.total_price_sats,
						params.natTerms.sats_per_nat
				  ]
				: null
		})
		let intent: string
		try {
			intent = verifyIntent(await lookItem())
		} catch (error) {
			if (!(error instanceof OfferError) || error.code !== 'offer_item_moved') throw error
			// The lookup and the build may straddle an index update: look once more.
			signal?.throwIfAborted()
			intent = verifyIntent(await lookItem())
		}

		await ensureEccLib()
		const intentEscrow = recoveryScripts({
			version: 2,
			intentCommitment: intent,
			offerId: build.offer_id,
			fundingTxid: '00'.repeat(32),
			vout: 0,
			value: build.escrow_value,
			destination: paymentAddress,
			buyerKey: publicKey.slice(-64),
			// Pinned in the client and verified against intent field 4 above.
			cosignerKey: OFFER_COSIGNER_PUBLIC_KEY,
			delay: build.recovery_delay_blocks
		})
		const escrowOutputs = parsePsbt(build.funding_psbt).txOutputs.filter(
			(output) => output.script.equals(intentEscrow.output) && output.value === build.escrow_value
		)
		if (escrowOutputs.length !== 1) throw new OfferError('offer_intent_mismatch')
		// The wallet signs every input of the funding it is handed, so pin the
		// whole template before anything is signed: only the buyer's own coins
		// go in, and only the escrow, the vault mint and change come out.
		validateOfferFundingTemplate(build.funding_psbt, {
			escrowScript: intentEscrow.output,
			escrowValue: build.escrow_value,
			paymentAddress,
			feeRate: params.feeRate
		})
		const intentFields = build.intent_fields as string[]

		if (params.natTerms || USE_V2_OFFERS_PREVIEW) rememberNatOffer(build.offer_id)
		if (
			params.natTerms &&
			(!build.nat_terms ||
				Object.keys(params.natTerms).some(
					(key) =>
						build.nat_terms?.[key as keyof NatTerms] !== params.natTerms?.[key as keyof NatTerms]
				))
		)
			throw new OfferError('invalid_nat_terms')

		signal?.throwIfAborted()
		if (batch) {
			const inputs = parsePsbt(build.funding_psbt).txInputs.map(
				(input) => `${Buffer.from(input.hash).toString('hex')}:${input.index}`
			)
			// A stale spendable-balance response must never make the next offer
			// replace a funding transaction already activated in this batch.
			if (inputs.some((input) => batch.usedInputs.has(input)))
				throw new OfferError('funding_inputs_pending')
			inputs.forEach((input) => batch.usedInputs.add(input))
		}
		const fundingInputs = parsePsbt(build.funding_psbt).txInputs.map(
			(input) => `${Buffer.from(input.hash).toString('hex')}:${input.index}`
		)
		const fundingPendingKey = `ow:pending-offer-funding:v1:${taproot}:${build.offer_id}`
		await assertNoPendingFundingOverlap(taproot, fundingInputs, signal)
		// Every wallet signs in two prompts: fund, then authorize the escrow.
		// UniSat's batch signer aborts a bundle holding a SIGHASH_NONE input
		// (how collection and trait bids authorize) and misbehaved on the
		// funding + item-bid pair too, so the build's `batch_accept` is
		// ignored and the proven sequential path is the only one.
		let signedAccept: string | undefined
		onStep?.('sign_funding')
		const [signedFunding] = await signOfferPsbts([ownedInputPlan(build.funding_psbt)], signal)
		signal?.throwIfAborted()
		// A wallet that resolves without signing (an unlock that never
		// happened, a provider returning nothing) must stop here, not post an
		// empty body to /prepare and read the API's 422 back to the buyer.
		if (!fundingLooksSigned(signedFunding)) throw new OfferError('funding_not_signed')
		// Every wallet type: the reviewed transaction, signed with ALL or DEFAULT only.
		assertSignedFunding(build.funding_psbt, signedFunding)
		onStep?.('preparing')
		const prep = await post<{ accept_psbt: string; sign_input_index: number }>(
			`/market/offers/${build.offer_id}/prepare`,
			{ funding_psbt: signedFunding }
		)
		onStep?.('sign_offer')
		signal?.throwIfAborted()

		await ensureEccLib()
		// Save public recovery material before anything can be broadcast.
		const backup = backupFromPsbt(
			prep.accept_psbt,
			prep.sign_input_index,
			build.offer_id,
			paymentAddress,
			build.recovery_delay_blocks
		)
		if (backup.version !== 2 || backup.intentCommitment !== intent)
			throw new OfferError('offer_intent_mismatch', `Offer authorization terms changed. ${NOT_PLACED}`)
		if (
			backup.buyerKey !== publicKey.slice(-64) ||
			backup.cosignerKey !== OFFER_COSIGNER_PUBLIC_KEY ||
			backup.delay !== build.recovery_delay_blocks
		)
			throw new Error(`The recovery key does not match your wallet. ${NOT_PLACED}`)
		const fundingPsbt = parsePsbt(signedFunding)
		const fundingTx = Transaction.fromBuffer(fundingPsbt.data.globalMap.unsignedTx.toBuffer())
		// Nested SegWit funding adds a redeem-script push to scriptSig during
		// finalization, which changes txid. Match the API's ordinary finalizer.
		fundingPsbt.data.inputs.forEach((input, index) => {
			if (input.finalScriptSig) fundingTx.setInputScript(index, input.finalScriptSig)
			else if (!input.finalScriptWitness && input.redeemScript && input.partialSig?.length === 1)
				fundingTx.setInputScript(index, bitcoinScript.compile([input.redeemScript]))
		})
		const fundingOutput = fundingPsbt.txOutputs[backup.vout]
		const escrowScripts = recoveryScripts(backup)
		const escrowScript = escrowScripts.output
		if (
			backup.fundingTxid !== fundingTx.getId() ||
			backup.value !== build.escrow_value ||
			!fundingOutput ||
			fundingOutput.value !== backup.value ||
			!fundingOutput.script.equals(escrowScript) ||
			!escrowScript.equals(intentEscrow.output)
		)
			throw new Error(`The recovery backup does not match the funding output. ${NOT_PLACED}`)
		saveRecoveryBackup(backup)
		// Pin the template the buyer is about to pre-sign to the offer it
		// reviewed: the item sale spends exactly the item and this escrow and
		// pays the buyer, the seller and the quoted fee; the collection or
		// trait authorization spends only this escrow.
		const escrow = {
			txid: backup.fundingTxid,
			vout: backup.vout,
			value: backup.value,
			script: escrowScript,
			saleLeaf: escrowScripts.sale,
			saleControl: escrowScripts.saleControl
		}
		if (scope === 'item')
			validateOfferAcceptTemplate(prep.accept_psbt, {
				signInputIndex: prep.sign_input_index,
				itemOutpoint: String(intentFields[13] ?? ''),
				itemValue: Number(intentFields[14]),
				sellerAddress: String(intentFields[15] ?? ''),
				buyerAddress: taproot,
				escrow,
				priceSats: Math.round(params.priceSats),
				marketFeeSats: build.market_fee_sats,
				feeAddress: OFFER_MARKET_FEE_ADDRESS
			})
		else validateOfferScopeTemplate(prep.accept_psbt, { signInputIndex: prep.sign_input_index, escrow })
		// Item offers pre-sign the exact sale (SIGHASH_ALL); collection and trait
		// offers pre-sign the escrow alone (NONE|ANYONECANPAY) and Ordinals
		// Wallet's co-signature enforces the fill policy.
		if (!signedAccept) {
			try {
				;[signedAccept] = await signOfferPsbts(
					[{ psbt: prep.accept_psbt, indexes: [prep.sign_input_index], tapscript: true }],
					signal
				)
			} catch (error) {
				// The funding is already signed: say what that means.
				if (error instanceof OfferError && error.code === 'offer_template_oversigns')
					throw new OfferError(error.code, `${error.message} ${NOT_PLACED}`)
				throw error
			}
		}
		signal?.throwIfAborted()
		// Every wallet type: a sale-leaf signature by this buyer with this
		// offer's sighash, nothing on the key path or any other leaf or input.
		assertSignedEscrow(prep.accept_psbt, signedAccept, {
			index: prep.sign_input_index,
			escrow,
			buyerXOnly: publicKey.slice(-64),
			sighash: scope === 'item' ? SIGHASH_ALL : SIGHASH_NONE_ANYONECANPAY
		})

		onStep?.('activating')
		localStorage.setItem(fundingPendingKey, JSON.stringify({ inputs: fundingInputs, at: Date.now() }))
		let activated: { offer: Offer; funding_txid: string }
		try {
			activated = await activateWithRetry(
				build.offer_id,
				{ funding_psbt: signedFunding, accept_psbt: signedAccept },
				signal
			)
		} catch (original) {
			// These answers come before a new journal reservation or broadcast
			// (preflight, a paused service, a rate limit): nothing is pending.
			if (original instanceof OfferError && OFFER_PRE_BROADCAST_CODES.has(original.code)) {
				localStorage.removeItem(fundingPendingKey)
				throw original
			}
			const reconciled = await reconcileQuietly(build.offer_id)
			if (
				reconciled?.offer?.id !== build.offer_id ||
				!['active', 'accepted'].includes(reconciled.offer.state) ||
				!reconciled.offer.funding_txid
			) {
				// The funding reached the node even though activation did not
				// answer: keep the mark on these coins and say so, never
				// "nothing was broadcast".
				if (fundingPendingFrom(reconciled))
					throw new OfferError('offer_funding_pending', undefined, {
						offerId: build.offer_id,
						detail: original instanceof OfferError ? original.detail : undefined,
						status: original instanceof OfferError ? original.status : undefined
					})
				// The API never took the signed offer: nothing is pending on these
				// coins, so the next offer must not be blocked by this attempt.
				if (isTransportFailure(original) && reconciled?.offer?.state === 'building')
					localStorage.removeItem(fundingPendingKey)
				if (isTransportFailure(original))
					throw new OfferError('activation_unreachable', undefined, {
						offerId: build.offer_id,
						detail: original instanceof OfferError ? original.detail : undefined,
						status: original instanceof OfferError ? original.status : undefined
					})
				throw original
			}
			activated = { offer: reconciled.offer, funding_txid: reconciled.offer.funding_txid }
		}
		if (
			activated.offer?.id !== build.offer_id ||
			!['active', 'accepted'].includes(activated.offer.state)
		) {
			throw new OfferError(
				'activation_unconfirmed',
				'The server did not confirm this offer’s activation.'
			)
		}
		onStep?.('done')
		localStorage.removeItem(fundingPendingKey)
		return activated.offer
	} finally {
		release()
	}
}

/**
 * Bid rebate: buying through a bid pays a smaller market fee than a listing
 * purchase, by this much of the bid. Applied by the API as a smaller fee output
 * (the buyer simply funds less), never below the 1,000-sat fee minimum.
 */
export const BID_REBATE_PERCENT = 1.69
export { BID_REBATE_BPS, BID_FEE_NET_MIN, bidFeeSplit, standardOfferFeeSats } from './offer-quote'

/** Smallest bid the API accepts: well above P2TR dust for the seller's payout. */
export const MIN_OFFER_SATS = 1_000
export const MIN_OFFER_BTC = '0.00001'
/** A transport-class failure: the request never got an answer from the API. */
export function isTransportFailure(error: unknown): boolean {
	if (!(error instanceof OfferError)) return false
	if (error.code === 'network_unreachable') return true
	const status = /^http_(\d{3})$/.exec(error.code)
	return !!status && [502, 503, 504, 408].includes(Number(status[1]))
}

/** The signed funding PSBT the wallet handed back is a real PSBT with a signature on it. */
export function fundingLooksSigned(psbtHex: unknown): boolean {
	if (typeof psbtHex !== 'string' || !psbtHex.trim()) return false
	try {
		const inputs = parsePsbt(psbtHex).data.inputs
		// Nothing to inspect (a stub in tests): the wallet answered, let the API judge.
		if (!inputs?.length) return true
		return inputs.some(
			(input) =>
				!!input.finalScriptWitness?.length ||
				!!input.finalScriptSig?.length ||
				!!input.tapKeySig?.length ||
				!!input.tapScriptSig?.length ||
				!!input.partialSig?.length
		)
	} catch {
		return false
	}
}

/** Reconcile says the funding is signed and with the node but not yet settled. */
const fundingPendingFrom = (reconciled: { offer?: Offer; funding_pending?: boolean } | null) =>
	reconciled?.funding_pending === true || reconciled?.offer?.funding_pending === true

async function reconcileQuietly(
	offerId: string
): Promise<{ offer?: Offer; funding_pending?: boolean } | null> {
	const response = await fetch(
		`${offerApi(`/market/offers/${offerId}/reconcile`)}/market/offers/${offerId}/reconcile`,
		{ cache: 'no-store', signal: AbortSignal.timeout(10000) }
	).catch(() => null)
	if (!response) return null
	if (response.status === 404) return { offer: undefined }
	return response.ok ? await response.json().catch(() => null) : null
}

const sleep = (ms: number, signal?: AbortSignal) =>
	new Promise<void>((resolve, reject) => {
		const timer = setTimeout(resolve, ms)
		signal?.addEventListener(
			'abort',
			() => {
				clearTimeout(timer)
				reject(signal.reason)
			},
			{ once: true }
		)
	})

/** Backoff between activation retries; about 30 s in total, matching a pod restart. */
export const ACTIVATE_RETRY_DELAYS_MS = [2_000, 4_000, 8_000, 16_000]

/**
 * Submit the signed bid, riding through a transport blip. Both signatures
 * are already in hand, so a preflight the proxy answered with 503 while the
 * API restarted must not cost the buyer the bid: wait, ask the API whether
 * the first attempt in fact landed, and post again if it did not.
 */
async function activateWithRetry(
	offerId: string,
	body: { funding_psbt: string; accept_psbt?: string },
	signal?: AbortSignal
): Promise<{ offer: Offer; funding_txid: string }> {
	let attempt = 0
	for (;;) {
		try {
			return await post<{ offer: Offer; funding_txid: string }>(
				`/market/offers/${offerId}/activate`,
				body
			)
		} catch (error) {
			if (!isTransportFailure(error) || attempt >= ACTIVATE_RETRY_DELAYS_MS.length) throw error
			await sleep(ACTIVATE_RETRY_DELAYS_MS[attempt++], signal)
			signal?.throwIfAborted()
			const reconciled = await reconcileQuietly(offerId)
			const state = reconciled?.offer?.state
			if (
				reconciled?.offer?.id === offerId &&
				(state === 'active' || state === 'accepted') &&
				reconciled.offer.funding_txid
			)
				return { offer: reconciled.offer, funding_txid: reconciled.offer.funding_txid }
			// Anything but "still waiting for the signed bid" means a retry
			// cannot help; surface the original transport error.
			if (reconciled?.offer && state !== 'building') throw error
		}
	}
}

/** Pending-funding marks older than this are stale by construction. */
const PENDING_FUNDING_TTL_MS = 6 * 60 * 60 * 1000

/**
 * The next bid must not spend coins a bid activated moments ago is spending:
 * the wallet's balance can lag the broadcast. A mark is only trusted while
 * the API agrees the offer took the funding; a mark left behind by an
 * attempt the API never received (state still `building`, or gone) is
 * dropped so one failed submit cannot block every later bid on those coins.
 */
async function assertNoPendingFundingOverlap(
	taproot: string,
	fundingInputs: string[],
	signal?: AbortSignal
): Promise<void> {
	const prefix = `ow:pending-offer-funding:v1:${taproot}:`
	const keys: string[] = []
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i)
		if (key?.startsWith(prefix)) keys.push(key)
	}
	for (const key of keys) {
		let pending: { inputs?: string[]; at?: number } | null = null
		try {
			pending = JSON.parse(localStorage.getItem(key) || 'null')
		} catch {}
		if (!pending?.inputs?.some((input) => fundingInputs.includes(input))) continue
		if (typeof pending.at === 'number' && Date.now() - pending.at > PENDING_FUNDING_TTL_MS) {
			localStorage.removeItem(key)
			continue
		}
		signal?.throwIfAborted()
		const reconciled = await reconcileQuietly(key.slice(prefix.length))
		const state = reconciled?.offer?.state
		// The API may still be waiting on the funding transaction it already
		// took; the mark stays until it says otherwise.
		if (reconciled && !fundingPendingFrom(reconciled) && (!reconciled.offer || state === 'building')) {
			localStorage.removeItem(key)
			continue
		}
		throw new OfferError('funding_inputs_pending')

	}
}

export const MAX_OFFER_QUANTITY = 10

export class OfferBatchError extends Error {
	constructor(
		readonly placed: Offer[],
		readonly quantity: number,
		readonly activationUncertain: boolean,
		cause: unknown
	) {
		const detail = cause instanceof Error ? cause.message : 'The offer could not be placed.'
		super(
			`${placed.length} of ${quantity} offers placed. ${detail}${
				activationUncertain
					? ' The last offer’s activation status is unknown; verify it before placing another batch.'
					: ' No further offers were submitted.'
			}`
		)
		this.name = 'OfferBatchError'
	}
}

/** Quantity means separately funded one-item offers, never repeated fills of
 * one escrow. Stop on the first failure; never automatically retry activation. */
export async function makeOfferBatch(
	params: MakeOfferParams,
	quantity: number,
	callbacks: {
		onStep?: (step: OfferStep, current: number) => void
		onPlaced?: (offer: Offer) => void
		signal?: AbortSignal
	} = {}
): Promise<Offer[]> {
	if (
		(!!params.natTerms && quantity !== 1) ||
		!Number.isInteger(quantity) ||
		quantity < 1 ||
		quantity > MAX_OFFER_QUANTITY ||
		((params.scope ?? 'item') === 'item' && quantity !== 1)
	) {
		throw new OfferError('invalid_quantity')
	}
	const taproot = Wallet.getTaprootAddress()
	const ordinalKey = Wallet.taprootPaymentPublicKey() || Wallet.getPublicKey()
	const publicKey = /^[0-9a-f]{64}$/i.test(ordinalKey) ? `02${ordinalKey}` : ordinalKey
	const batch = {
		usedInputs: new Set<string>(),
		buyer: JSON.stringify([
			taproot,
			publicKey,
			Wallet.balanceAddress() || taproot,
			Wallet.balancePublicKey() || publicKey
		])
	}
	const placed: Offer[] = []
	for (let current = 1; current <= quantity; current++) {
		let activating = false
		let offer: Offer
		try {
			offer = await makeOffer(
				params,
				(step) => {
					activating = step === 'activating'
					if (step !== 'done') callbacks.onStep?.(step, current)
				},
				batch,
				callbacks.signal
			)
		} catch (error) {
			// A definitive pre-broadcast answer (paused, rate limited, preflight)
			// at activation leaves nothing uncertain about this offer.
			const definitive = error instanceof OfferError && OFFER_PRE_BROADCAST_CODES.has(error.code)
			throw new OfferBatchError([...placed], quantity, activating && !definitive, error)
		}
		placed.push(offer)
		callbacks.onPlaced?.(offer)
	}
	callbacks.onStep?.('done', quantity)
	return placed
}
